import React, { useState, useEffect, useRef } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { UserPlus, Mail, Trash2, Shield, User, Clock, Check, X } from 'lucide-react';
import { checkTeamMemberLimit, getTeamMemberStats } from '../../services/usageService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

const OrgUserManagement = () => {
  const { currentUser, currentOrg, userRole, refreshOrganizationData } = useAuth();
  const t = useTranslation();
  const [users, setUsers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
    const [teamMemberStats, setTeamMemberStats] = useState(null);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const emailInputRef = useRef(null);



  // DEBUG: Add logging for auth context
  console.log('OrgUserManagement - Auth Context:', {
    currentUser: currentUser ? {
      uid: currentUser.uid,
      email: currentUser.email
    } : null,
    userRole: userRole,
    currentOrg: currentOrg ? {
      id: currentOrg.id,
      name: currentOrg.name,
      memberCount: currentOrg.memberCount,
      pendingInvitesCount: currentOrg.pendingInvitesCount
    } : null
  });

  // Use org-level fields for seat logic
  const totalActive = currentOrg?.memberCount || 0;
  const totalPending = userRole === 'admin' ? pendingInvites.length : (currentOrg?.pendingInvitesCount || 0);
  const teamLimit = teamMemberStats?.limit || 0;
  const seatsLeft = teamLimit - (totalActive + totalPending);
  const atOrOverInviteLimit = seatsLeft <= 0;

  // DEBUG: Add logging for calculated values
  console.log('OrgUserManagement - Calculated values:', {
    totalActive,
    totalPending,
    teamLimit,
    seatsLeft,
    atOrOverInviteLimit,
    isAdmin: userRole === 'admin',
    userRole: userRole
  });

  // Only fetch users and pendingInvites if admin
  useEffect(() => {
    console.log('OrgUserManagement - useEffect triggered:', {
      userRole: userRole,
      currentOrgId: currentOrg?.id,
      isAdmin: userRole === 'admin'
    });

    if (userRole === 'admin') {
      console.log('OrgUserManagement - User is admin, fetching data...');
      fetchUsers();
      fetchPendingInvites();
    } else {
      console.log('OrgUserManagement - User is not admin, skipping user/invite fetch');
      setLoading(false);
    }
    fetchTeamMemberStats();
  }, [currentOrg, userRole]);

  const fetchTeamMemberStats = async () => {
    console.log('OrgUserManagement - fetchTeamMemberStats called:', { orgId: currentOrg?.id });
    
    if (!currentOrg?.id) {
      console.log('OrgUserManagement - No org ID, skipping team member stats');
      return;
    }
    
    try {
      console.log('OrgUserManagement - Calling getTeamMemberStats...');
      const stats = await getTeamMemberStats(currentOrg.id);
      console.log('OrgUserManagement - Team member stats received:', stats);
      setTeamMemberStats(stats);
    } catch (err) {
      console.error('OrgUserManagement - Error fetching team member stats:', err);
      setError(`Failed to fetch team member stats: ${err.message}`);
    }
  };

  const fetchUsers = async () => {
    console.log('OrgUserManagement - fetchUsers called:', { orgId: currentOrg?.id });
    
    if (!currentOrg) {
      console.log('OrgUserManagement - No currentOrg, skipping user fetch');
      return;
    }
    
    try {
      console.log('OrgUserManagement - Building users query...');
      const usersQuery = query(
        collection(db, 'users'),
        where('orgId', '==', currentOrg.id),
        where('status', '==', 'active')
      );
      
      console.log('OrgUserManagement - Executing users query...');
      const snapshot = await getDocs(usersQuery);
      console.log('OrgUserManagement - Users query completed:', {
        docsCount: snapshot.docs.length,
        isEmpty: snapshot.empty
      });
      
      const userData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('OrgUserManagement - Users data processed:', userData);
      setUsers(userData);
    } catch (err) {
      console.error('OrgUserManagement - Error fetching users:', err);
      setError(`Failed to load users: ${err.message}`);
    }
  };

  const fetchPendingInvites = async () => {
    console.log('OrgUserManagement - fetchPendingInvites called:', { orgId: currentOrg?.id });
    
    if (!currentOrg) {
      console.log('OrgUserManagement - No currentOrg, skipping invite fetch');
      return;
    }
    
    try {
      console.log('OrgUserManagement - Building invites query...');
      const invitesQuery = query(
        collection(db, 'users'),
        where('orgId', '==', currentOrg.id),
        where('status', '==', 'invited')
      );
      
      console.log('OrgUserManagement - Executing invites query...');
      const snapshot = await getDocs(invitesQuery);
      console.log('OrgUserManagement - Invites query completed:', {
        docsCount: snapshot.docs.length,
        isEmpty: snapshot.empty
      });
      
      const inviteData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('OrgUserManagement - Invites data processed:', inviteData);
      setPendingInvites(inviteData);
    } catch (err) {
      console.error('OrgUserManagement - Error fetching pending invites:', err);
      setError(`Failed to load pending invites: ${err.message}`);
    } finally {
      console.log('OrgUserManagement - Setting loading to false');
      setLoading(false);
    }
  };

  const generateInviteToken = () => {
    return Math.random().toString(36).substring(2, 15) + 
           Math.random().toString(36).substring(2, 15);
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      setError(t('orgAdmin.userManagement.emailRequired'));
      return;
    }

    setInviteLoading(true);
    setError('');

    try {
      // Use the secure Firebase function to invite user
      const inviteUserFunction = httpsCallable(functions, 'inviteUser');
      const result = await inviteUserFunction({
        email: inviteEmail.trim(),
        role: inviteRole
      });

      console.log('User invited successfully:', result.data);
      console.log(`Invitation sent to ${inviteEmail} with token: ${result.data.inviteToken}`);
      
      // Reset form
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteForm(false);
      
      // Refresh data
      await fetchPendingInvites();
      await fetchTeamMemberStats();
      
    } catch (err) {
      console.error('Error inviting user:', err);
      if (err.code === 'functions/already-exists') {
        setError(t('orgAdmin.userManagement.userAlreadyExists'));
      } else if (err.code === 'functions/resource-exhausted') {
        setError(t('orgAdmin.userManagement.teamMemberLimit.cannotInvite'));
      } else if (err.code === 'functions/permission-denied') {
        setError('Only organization admins can invite users.');
      } else if (err.code === 'functions/invalid-argument') {
        setError(err.message || 'Invalid invitation details provided.');
      } else {
        setError(t('orgAdmin.userManagement.inviteUserFailed'));
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: serverTimestamp()
      });
      
      // Refresh users
      await fetchUsers();
    } catch (err) {
      console.error('Error updating user role:', err);
      setError(t('orgAdmin.userManagement.updateRoleFailed'));
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      // Use the secure Firebase function to cancel invitation
      const cancelInvitationFunction = httpsCallable(functions, 'cancelInvitation');
      const result = await cancelInvitationFunction({ inviteId });

      console.log('Invitation canceled successfully:', result.data);
      
      // Refresh data
      await fetchPendingInvites();
      await fetchTeamMemberStats();
    } catch (err) {
      console.error('Error canceling invite:', err);
      if (err.code === 'functions/not-found') {
        setError('Invitation not found or already canceled.');
      } else if (err.code === 'functions/permission-denied') {
        setError('Only organization admins can cancel invitations.');
      } else if (err.code === 'functions/failed-precondition') {
        setError('Can only cancel pending invitations.');
      } else {
        setError(t('orgAdmin.userManagement.cancelInviteFailed'));
      }
    }
  };

  const handleRemoveUser = (userId) => {
    setUserToRemove(userId);
    setShowConfirmRemove(true);
  };

  const confirmRemoveUser = async () => {
    if (!userToRemove) return;

    try {
      // Update user status to removed
      await updateDoc(doc(db, 'users', userToRemove), {
        status: 'removed',
        updatedAt: serverTimestamp()
      });

      // Update organization member count
      const currentCount = currentOrg?.memberCount || 0;
      const newCount = Math.max(0, currentCount - 1);
      
      await updateDoc(doc(db, 'organizations', currentOrg.id), {
        memberCount: newCount,
        updatedAt: serverTimestamp()
      });
      
      // Instead of multiple re-renders, just update the local state directly
      // Remove the user from the local users array
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userToRemove));
      
      // Update the currentOrg memberCount in the auth context
      // This is a bit of a hack, but it avoids the re-render issues
      if (currentOrg) {
        const updatedOrg = { ...currentOrg, memberCount: Math.max(0, (currentOrg.memberCount || 0) - 1) };
        // We can't directly update currentOrg from here, so let's just refresh team stats
        await fetchTeamMemberStats();
      }
    } catch (err) {
      console.error('Error removing user:', err);
      setError(t('orgAdmin.userManagement.removeUserFailed'));
    } finally {
      setShowConfirmRemove(false);
      setUserToRemove(null);
    }
  };

  const cancelRemoveUser = () => {
    setShowConfirmRemove(false);
    setUserToRemove(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Invite Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            {t('orgAdmin.userManagement.title')}
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            {t('orgAdmin.userManagement.subtitle')}
          </p>
          {teamMemberStats && (
            <div className="mt-2">
              <span className={`text-sm font-medium ${
                teamMemberStats.isAtLimit 
                  ? 'text-red-600 dark:text-red-400' 
                  : teamMemberStats.isNearLimit 
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-green-600 dark:text-green-400'
              }`}>
                {t('orgAdmin.userManagement.teamMemberLimit.currentCount', {
                  current: teamMemberStats.currentCount,
                  limit: teamMemberStats.limit
                })}
              </span>
              <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                {t('orgAdmin.userManagement.teamMemberLimit.warning', {
                  current: totalActive + totalPending,
                  limit: teamLimit
                })}
                {seatsLeft > 0 && (
                  <> — {seatsLeft} {seatsLeft === 1 ? 'seat' : 'seats'} left</>
                )}
                {totalPending > 0 && (
                  <> — {totalPending} pending</>
                )}
              </span>
              {atOrOverInviteLimit && (
                <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {t('orgAdmin.userManagement.teamMemberLimit.cannotInvite')}
                </div>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowInviteForm(true)}
          disabled={teamMemberStats && atOrOverInviteLimit}
          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
            teamMemberStats && atOrOverInviteLimit
              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
          title={teamMemberStats && atOrOverInviteLimit 
            ? t('orgAdmin.userManagement.teamMemberLimit.cannotInvite')
            : ''
          }
        >
          <UserPlus className="w-4 h-4 mr-2" />
          {t('orgAdmin.userManagement.inviteUser')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Invite Form Modal */}
      {showInviteForm && currentOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {t('orgAdmin.userManagement.inviteNewUser')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('orgAdmin.userManagement.emailAddress')}
                </label>
                <input
                  ref={emailInputRef}
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={t('orgAdmin.userManagement.emailPlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 relative z-10"
                  disabled={inviteLoading}
                  autoFocus
                  style={{ pointerEvents: 'auto' }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {t('orgAdmin.userManagement.role')}
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={inviteLoading}
                >
                  <option value="member">{t('orgAdmin.userManagement.roles.member')}</option>
                  <option value="admin">{t('orgAdmin.userManagement.roles.admin')}</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteForm(false);
                  setInviteEmail('');
                  setInviteRole('member');
                  setError('');
                }}
                disabled={inviteLoading}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleInviteUser}
                disabled={inviteLoading || !inviteEmail.trim()}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center"
              >
                {inviteLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                {t('orgAdmin.userManagement.sendInvitation')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Modal */}
      {showConfirmRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {t('orgAdmin.userManagement.confirmRemoveUser')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Are you sure you want to remove this user from your organization? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelRemoveUser}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmRemoveUser}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                Remove User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Invitations */}
      {userRole === 'admin' && pendingInvites.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
          <h3 className="font-medium text-yellow-800 dark:text-yellow-300 mb-3 flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            {t('orgAdmin.userManagement.pendingInvitations')} ({pendingInvites.length})
          </h3>
          <div className="space-y-2">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="flex items-center justify-between bg-white dark:bg-slate-700 p-3 rounded border">
                <div>
                  <span className="font-medium text-slate-800 dark:text-slate-200">{invite.email}</span>
                  <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">
                    ({t(`orgAdmin.userManagement.roles.${invite.role}`)})
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {t('orgAdmin.userManagement.inviteCode')}: {invite.inviteToken}
                  </span>
                  <button
                    onClick={() => handleCancelInvite(invite.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title={t('orgAdmin.userManagement.cancelInvitation')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Users */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100">
            {t('orgAdmin.userManagement.activeMembers')} ({users.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="bg-slate-50 dark:bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('orgAdmin.userManagement.user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('orgAdmin.userManagement.role')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('orgAdmin.userManagement.joined')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('orgAdmin.userManagement.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                          {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {user.displayName || user.email}
                        </div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {user.role === 'admin' ? (
                        <Shield className="w-4 h-4 text-blue-500 mr-2" />
                      ) : (
                        <User className="w-4 h-4 text-slate-400 mr-2" />
                      )}
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        disabled={user.id === currentUser?.uid} // Can't change your own role
                        className="text-sm border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 disabled:opacity-50"
                      >
                        <option value="member">{t('orgAdmin.userManagement.roles.member')}</option>
                        <option value="admin">{t('orgAdmin.userManagement.roles.admin')}</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                    {user.createdAt?.toDate?.()?.toLocaleDateString() || t('orgAdmin.userManagement.unknown')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {user.id !== currentUser?.uid && (
                      <button
                        onClick={() => handleRemoveUser(user.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrgUserManagement; 